import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Activity, Wind, Soup, Heart, Bone, Shield,
  ArrowRight, Search, BookOpen, Sparkles,
  GraduationCap, Languages, Video, Mic,
} from "lucide-react";
import { getCategories, getStats } from "../lib/api";
import { useLanguage } from "../context/LanguageContext";
import HijamaSunnahDaysCard from "../components/HijamaSunnahDaysCard";
import TodayCalendarCard from "../components/TodayCalendarCard";
import ShareOnWhatsApp from "../components/ShareOnWhatsApp";
import TranslationNotice from "../components/TranslationNotice";

const LOGO_URL =
  "https://customer-assets.emergentagent.com/job_tibb-nabawi-db/artifacts/wvm4vikx_Screenshot_20260708_041833_com_whatsapp_w4b_MediaViewActivity.jpg";

const ICONS = { Activity, Wind, Soup, Heart, Bone, Shield };

const HERO_BG = "https://images.unsplash.com/photo-1745760883775-47d096603d5c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NjV8MHwxfHNlYXJjaHwxfHxpc2xhbWljJTIwYXJjaGl0ZWN0dXJlJTIwZWxlZ2FudCUyMGludGVyaW9yJTIwbGlnaHR8ZW58MHx8fHwxNzc4NjI3MjkwfDA&ixlib=rb-4.1.0&q=85";

const ACADEMY_IMG = "https://images.unsplash.com/photo-1725007995233-0d5b8bfe8f30?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NjZ8MHwxfHNlYXJjaHwzfHxvcGVuJTIwUXVyYW4lMjBtdXNoYWYlMjBnb2xkZW4lMjBjYWxsaWdyYXBoeXxlbnwwfHx8fDE3ODk1ODExODJ8MA&ixlib=rb-4.1.0&q=85";

const REMEDY_IMG = {
  honey: "https://images.unsplash.com/photo-1694457331480-b709d8496b1a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNDR8MHwxfHNlYXJjaHwxfHxvcmdhbmljJTIwcmF3JTIwaG9uZXklMjBqYXIlMjBuYXR1cmFsfGVufDB8fHx8MTc3ODYyNzI2OHww&ixlib=rb-4.1.0&q=85",
  blackseed: "https://images.unsplash.com/photo-1735011725748-9214b960266b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzR8MHwxfHNlYXJjaHwxfHxibGFjayUyMHNlZWQlMjBvaWwlMjBoZWFsdGh5JTIwbmF0dXJhbHxlbnwwfHx8fDE3Nzg2MjcyNjh8MA&ixlib=rb-4.1.0&q=85",
  cupping: "https://images.unsplash.com/photo-1598555748505-ccca0d9b9f7b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzZ8MHwxfHNlYXJjaHwxfHxjdXBwaW5nJTIwdGhlcmFweSUyMGhlYWx0aHxlbnwwfHx8fDE3Nzg2MjcyOTB8MA&ixlib=rb-4.1.0&q=85",
};

const CATEGORY_AR_MAP = {
  chronic: "الأمراض المزمنة",
  respiratory: "أمراض الجهاز التنفسي",
  digestive: "أمراض الجهاز الهضمي",
  mental_spiritual: "الأمراض النفسية والروحية",
  musculoskeletal: "الجهاز العضلي الهيكلي",
  skin_immunity: "الجلد والمناعة",
};

const CATEGORY_NAME_AR = {
  chronic: "الأمراض المزمنة",
  respiratory: "الجهاز التنفسي",
  digestive: "الجهاز الهضمي",
  mental_spiritual: "نفسية وروحية",
  musculoskeletal: "العضلي الهيكلي",
  skin_immunity: "الجلد والمناعة",
};

const CATEGORY_DESC_AR = {
  chronic: "حالات طويلة الأمد كالسكري وارتفاع الضغط والكوليسترول وأمراض القلب.",
  respiratory: "الربو والتهاب الجيوب والالتهاب الشعبي والحساسية.",
  digestive: "القولون العصبي والتهاب المعدة والإمساك وصحة الكبد.",
  mental_spiritual: "القلق والاكتئاب والأرق والعين والحماية الروحية من السحر.",
  musculoskeletal: "عرق النسا وآلام الظهر والتهاب المفاصل والصداع النصفي.",
  skin_immunity: "الإكزيما والصدفية ودعم المناعة الذاتية.",
};

const Home = () => {
  const [categories, setCategories] = useState([]);
  const [stats, setStats] = useState(null);
  const { t, lang } = useLanguage();

  useEffect(() => {
    getCategories(lang).then((d) => setCategories(d.categories || []));
    getStats().then(setStats).catch(() => {});
  }, [lang]);

  return (
    <div data-testid="home-page" className="bg-grain">
      {/* Today's dual-calendar strip (Gregorian + Hijri, live, localised) */}
      <TodayCalendarCard />

      <section data-testid="hero-section" className="relative overflow-hidden">
        <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url(${HERO_BG})` }} />
        <div className="absolute inset-0 bg-sand/60" />
        <div className="absolute inset-0 bg-islamic-pattern opacity-40" />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-8 py-20 sm:py-32">
          <div className="grid lg:grid-cols-5 gap-12 items-center">
            <div className="lg:col-span-3">
            <p data-testid="hero-arabic" className="arabic text-2xl sm:text-3xl text-gold mb-6 animate-fade-in-up">
              وَنُنَزِّلُ مِنَ الْقُرْآنِ مَا هُوَ شِفَاءٌ وَرَحْمَةٌ لِّلْمُؤْمِنِينَ
            </p>
            <h1 data-testid="hero-title" className="font-heading text-4xl sm:text-5xl lg:text-6xl text-forest font-bold leading-tight tracking-tight mb-6 animate-fade-in-up">
              {t("hero_title_1")}
              <span className="block text-gold italic mt-2">{t("hero_title_2")}</span>
            </h1>
            <p data-testid="hero-subtitle" className="text-lg text-mute leading-relaxed mb-10 max-w-2xl animate-fade-in-up">
              {t("hero_subtitle")}
            </p>
            <div className="flex flex-wrap gap-4 animate-fade-in-up">
              <Link to="/search" data-testid="cta-ai-search" className="inline-flex items-center gap-2 bg-forest text-sand hover:bg-sage rounded-full px-7 py-4 text-sm font-medium tracking-wide transition-all hover:-translate-y-0.5 shadow-lg shadow-forest/20">
                <Sparkles className="w-4 h-4" />
                {t("cta_ai")}
              </Link>
              <a href="#categories" data-testid="cta-browse" className="inline-flex items-center gap-2 bg-cream text-forest hover:bg-white rounded-full px-7 py-4 text-sm font-medium tracking-wide transition-all hover:-translate-y-0.5 border border-forest/10">
                {t("cta_browse")}
                <ArrowRight className="w-4 h-4 rtl:rotate-180" />
              </a>
            </div>

            {stats && (
              <div data-testid="hero-stats" className="mt-14 grid grid-cols-2 sm:grid-cols-4 gap-4 max-w-2xl">
                {[
                  { key: "stat_illnesses", value: stats.total_illnesses },
                  { key: "stat_hadiths", value: stats.total_hadiths },
                  { key: "stat_points", value: stats.total_hijama_points },
                  { key: "stat_studies", value: stats.total_modern_studies },
                ].map((s) => (
                  <div key={s.key} className="clay-surface-soft rounded-2xl p-4 text-center">
                    <div className="font-heading text-3xl text-forest font-bold">{s.value}+</div>
                    <div className="text-[10px] uppercase tracking-[0.18em] text-gold mt-1">{t(s.key)}</div>
                  </div>
                ))}
              </div>
            )}
            </div>

            {/* Logo emblem — official Islamic Cure & Care brand mark */}
            <div data-testid="hero-logo" className="lg:col-span-2 hidden lg:flex items-center justify-center relative">
              <div className="absolute inset-0 bg-gold/10 rounded-full blur-3xl scale-75 animate-gentle-pulse" />
              <div className="relative animate-fade-in-up">
                <img
                  src={LOGO_URL}
                  alt="Islamic Cure & Care"
                  className="w-[420px] max-w-full object-contain drop-shadow-xl"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      <section id="categories" data-testid="categories-section" className="max-w-7xl mx-auto px-4 sm:px-8 py-20">
        {/* Hijama Sunnah Days — surfaces the next 17/19/21 Hijri days with one-tap WhatsApp booking */}
        <div className="mb-14">
          <HijamaSunnahDaysCard />
        </div>

        <div className="mb-12">
          <p className="text-xs uppercase tracking-[0.25em] text-gold font-medium mb-3">{t("library_label")}</p>
          <h2 data-testid="categories-title" className="font-heading text-3xl sm:text-4xl lg:text-5xl text-forest font-semibold tracking-tight max-w-3xl">
            {t("library_title")}
          </h2>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 stagger">
          {categories.map((cat) => {
            const Icon = ICONS[cat.icon] || BookOpen;
            return (
              <Link
                key={cat.id}
                to={`/category/${cat.id}`}
                data-testid={`category-card-${cat.id}`}
                className="clay-surface rounded-3xl p-8 lift-on-hover group block"
              >
                <div className="flex items-start justify-between mb-6">
                  <div className="w-14 h-14 rounded-2xl flex items-center justify-center" style={{ backgroundColor: `${cat.color}15`, color: cat.color }}>
                    <Icon className="w-7 h-7" />
                  </div>
                  <ArrowRight className="w-5 h-5 text-mute group-hover:text-forest group-hover:translate-x-1 rtl:rotate-180 transition-all" />
                </div>
                <p className="arabic text-gold text-xl mb-2">{cat.arabic}</p>
                <h3 className="font-heading text-2xl text-forest font-semibold mb-3">
                  {lang === "ar" ? (CATEGORY_NAME_AR[cat.id] || cat.name) : cat.name}
                </h3>
                <p className="text-sm text-mute leading-relaxed">
                  {lang === "ar" ? (CATEGORY_DESC_AR[cat.id] || cat.description) : cat.description}
                </p>
              </Link>
            );
          })}
        </div>
      </section>

      <section data-testid="remedies-section" className="max-w-7xl mx-auto px-4 sm:px-8 py-20">
        <div className="mb-12 max-w-3xl">
          <p className="text-xs uppercase tracking-[0.25em] text-gold font-medium mb-3">{t("pharmacy_label")}</p>
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl text-forest font-semibold tracking-tight mb-4">
            {t("pharmacy_title")}
          </h2>
          <p className="text-mute leading-relaxed">{t("pharmacy_subtitle")}</p>
        </div>

        <div className="grid md:grid-cols-3 gap-6">
          {[
            { key: "blackseed", img: REMEDY_IMG.blackseed, title: lang === "ar" ? "الحبة السوداء" : "Black Seed", arabic: "الحبة السوداء", quote: lang === "ar" ? "في الحبة السوداء شفاء من كل داء إلا السام." : "In it is healing for every disease except death.", ref: "Sahih al-Bukhari 5687" },
            { key: "honey", img: REMEDY_IMG.honey, title: lang === "ar" ? "العسل الخام" : "Raw Honey", arabic: "العسل", quote: lang === "ar" ? "يخرج من بطونها شراب مختلف ألوانه فيه شفاء للناس." : "From their bellies emerges a drink in which there is healing for people.", ref: "Quran 16:69" },
            { key: "cupping", img: REMEDY_IMG.cupping, title: lang === "ar" ? "الحجامة" : "Hijama (Cupping)", arabic: "الحجامة", quote: lang === "ar" ? "نِعمَ الدواءُ الحجامة." : "The best remedy you have is Hijama.", ref: "Sunan Ibn Majah 3476" },
          ].map((r) => (
            <div key={r.key} data-testid={`remedy-card-${r.key}`} className="rounded-3xl overflow-hidden clay-surface lift-on-hover">
              <div className="aspect-[4/3] overflow-hidden">
                <img src={r.img} alt={r.title} className="w-full h-full object-cover transition-transform duration-700 hover:scale-110" />
              </div>
              <div className="p-7">
                <p className="arabic text-gold text-xl mb-1">{r.arabic}</p>
                <h3 className="font-heading text-2xl text-forest font-semibold mb-3">{r.title}</h3>
                <blockquote className="text-sm text-mute leading-relaxed italic border-l-2 border-gold pl-4 mb-3 rtl:border-l-0 rtl:border-r-2 rtl:pl-0 rtl:pr-4">
                  "{r.quote}"
                </blockquote>
                <p className="text-xs text-forest font-medium">{r.ref}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section data-testid="academy-promo-section" className="max-w-7xl mx-auto px-4 sm:px-8 py-20">
        <div className="rounded-3xl overflow-hidden clay-surface grid lg:grid-cols-2 lift-on-hover">
          {/* Poster image — illuminated Qur'an */}
          <div className="relative min-h-[260px] lg:min-h-full overflow-hidden order-1 lg:order-2">
            <img src={ACADEMY_IMG} alt={lang === "ar" ? "أكاديمية القرآن والعربية" : "Qur'an & Arabic Academy"} className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 hover:scale-105" />
            <div className="absolute inset-0 bg-gradient-to-t from-forest/50 via-transparent to-transparent lg:bg-gradient-to-r lg:from-forest/30 lg:to-transparent" />
          </div>

          {/* Text side */}
          <div className="p-10 sm:p-14 bg-forest text-sand relative overflow-hidden order-2 lg:order-1">
            <div className="absolute inset-0 bg-islamic-pattern opacity-10" />
            <div className="relative">
              <div className="inline-flex items-center gap-2 bg-sand/10 text-sand rounded-full px-4 py-1.5 text-xs mb-6">
                <GraduationCap className="w-3.5 h-3.5" />
                {lang === "ar" ? "جديد · الأكاديمية" : "New · The Academy"}
              </div>
              <p className="arabic text-gold text-2xl mb-3">أكاديمية القرآن واللغة العربية</p>
              <h2 data-testid="academy-promo-title" className="font-heading text-3xl sm:text-4xl lg:text-5xl font-semibold leading-tight mb-5">
                {lang === "ar" ? "تعلّم القرآن والعربية مع معلّمين مباشرين" : "Learn Qur'an & Arabic with live tutors"}
              </h2>
              <p className="text-sand/80 leading-relaxed mb-7 max-w-xl">
                {lang === "ar"
                  ? "تلاوة القرآن والتجويد، وتعلّم اللغة العربية، ومدرّب النطق بالذكاء الاصطناعي — مع جلسات مباشرة فردية عبر الإنترنت مع معلّمين مؤهّلين."
                  : "Qur'an recitation & Tajweed, Arabic language learning, and an AI pronunciation coach — plus live one-to-one online sessions with qualified tutors."}
              </p>

              <ul className="flex flex-wrap gap-2.5 mb-9">
                {[
                  { icon: BookOpen, en: "Qur'an learning", ar: "تعلّم القرآن" },
                  { icon: Languages, en: "Arabic language", ar: "اللغة العربية" },
                  { icon: Video, en: "Online tutor sessions", ar: "جلسات مباشرة" },
                  { icon: Mic, en: "AI pronunciation", ar: "النطق بالذكاء" },
                ].map((f) => {
                  const FIcon = f.icon;
                  return (
                    <li key={f.en} className="inline-flex items-center gap-2 bg-sand/10 border border-sand/15 rounded-full px-4 py-2 text-xs sm:text-sm">
                      <FIcon className="w-4 h-4 text-gold" />
                      {lang === "ar" ? f.ar : f.en}
                    </li>
                  );
                })}
              </ul>

              <div className="mb-6"><TranslationNotice variant="badge" /></div>

              <div className="flex flex-wrap items-center gap-3">
                <Link to="/academy" data-testid="academy-promo-cta" className="inline-flex items-center gap-2 bg-sand text-forest hover:bg-cream rounded-full px-7 py-4 text-sm font-medium tracking-wide transition-all hover:-translate-y-0.5">
                  <GraduationCap className="w-4 h-4" />
                  {lang === "ar" ? "زيارة الأكاديمية" : "Visit the Academy"}
                  <ArrowRight className="w-4 h-4 rtl:rotate-180" />
                </Link>
                <ShareOnWhatsApp
                  title={lang === "ar" ? "أكاديمية القرآن واللغة العربية" : "Qur'an & Arabic Academy"}
                  url={typeof window !== "undefined" ? `${window.location.origin}/academy` : "/academy"}
                  className="!py-3.5"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      <section data-testid="ai-cta-section" className="max-w-7xl mx-auto px-4 sm:px-8 py-20">
        <div className="rounded-3xl bg-forest text-sand p-10 sm:p-16 relative overflow-hidden">
          <div className="absolute inset-0 bg-islamic-pattern opacity-10" />
          <div className="relative max-w-3xl">
            <div className="inline-flex items-center gap-2 bg-sand/10 text-sand rounded-full px-4 py-1.5 text-xs mb-6">
              <Sparkles className="w-3.5 h-3.5" />
              {t("ai_cta_pill")}
            </div>
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-semibold leading-tight mb-5">
              {t("ai_cta_title")}
            </h2>
            <p className="text-sand/80 leading-relaxed mb-8">{t("ai_cta_subtitle")}</p>
            <Link to="/search" data-testid="ai-cta-button" className="inline-flex items-center gap-2 bg-sand text-forest hover:bg-cream rounded-full px-7 py-4 text-sm font-medium tracking-wide transition-all hover:-translate-y-0.5">
              <Search className="w-4 h-4" />
              {t("ai_cta_btn")}
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
